// src/pages/components/CommentSection.tsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AtSign, Loader2, MessageSquare, Pencil, Reply, SendHorizontal, Smile, Trash2 } from 'lucide-react';
import { tasksApi, Task, TaskAssignee, TaskComment } from '../api/tasks';
import { useUserInfo } from '../hooks/useUserInfo';
import { formatTaskApiError } from '../utils/taskWorkloadCapacity';
import {
  buildMentionCandidates,
  extractMentionIds,
  filterMentionCandidates,
  findMentionQuery,
  insertMention,
  splitMentions,
  type MentionCandidate,
} from '../utils/taskMentions';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import ConfirmDialog, { ConfirmState } from './ConfirmDialog';
import PersonAvatar from './people/PersonAvatar';

type CommentTask = Pick<Task, 'created_by'> &
  Partial<Pick<Task, 'created_by_name' | 'primary_assignee_id' | 'primary_assignee_name'>>;

interface CommentSectionProps {
  taskId: string;
  comments: TaskComment[];
  onRefresh: () => void;
  /** The task and its assignees decide who can be @mentioned — task members only. */
  task: CommentTask | null;
  assignees: TaskAssignee[];
}

const QUICK_EMOJIS = [
  '👍', '🙏', '✅', '🎉', '🔥', '👀', '😊', '😂',
  '❤️', '👏', '🚀', '💯', '🤝', '🙌', '💡', '⏰',
  '📌', '📎', '📝', '⚠️', '❌', '❓', '🤔', '😅',
];
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
const COMPOSER_MAX_HEIGHT = 260;
const MENTION_MENU_WIDTH = 280;
const PILL_STYLE: React.CSSProperties = { padding: '2px 8px', fontSize: 10.5 };

function formatRelative(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

// Pixel position of a character inside a textarea (relative to its border box),
// measured with an off-screen mirror that copies the textarea's text metrics.
function getCaretCoordinates(el: HTMLTextAreaElement, position: number): { top: number; left: number } {
  const div = document.createElement('div');
  const style = window.getComputedStyle(el);
  const propsToCopy = [
    'boxSizing', 'width',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize',
    'fontSizeAdjust', 'lineHeight', 'fontFamily',
    'textAlign', 'textTransform', 'textIndent', 'textDecoration',
    'letterSpacing', 'wordSpacing', 'tabSize',
  ];
  propsToCopy.forEach((p) => {
    (div.style as any)[p] = (style as any)[p];
  });
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordWrap = 'break-word';
  div.style.overflow = 'hidden';
  div.style.top = '0';
  div.style.left = '-9999px';

  div.textContent = el.value.substring(0, position);
  const span = document.createElement('span');
  span.textContent = el.value.substring(position) || '.';
  div.appendChild(span);
  document.body.appendChild(div);
  const coords = {
    top: span.offsetTop + parseInt(style.borderTopWidth || '0', 10) - el.scrollTop,
    left: span.offsetLeft + parseInt(style.borderLeftWidth || '0', 10) - el.scrollLeft,
  };
  document.body.removeChild(div);
  return coords;
}

function CommentSection({ taskId, comments, onRefresh, task, assignees }: CommentSectionProps) {
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [focused, setFocused] = useState(false);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mentionPos, setMentionPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [showEmoji, setShowEmoji] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  // Caret to restore after a programmatic edit (mention pick, emoji, reply),
  // applied once React has written the new value into the textarea.
  const pendingCaret = useRef<{ pos: number; openMention: boolean } | null>(null);
  const { currentUser, getUser, getUserName, getUserEmail } = useUserInfo();

  const currentUserId = currentUser?.id ?? null;
  // Everyone involved in the task (incl. you) — drives role badges and mention highlights.
  const members = useMemo(
    () => (task ? buildMentionCandidates({ task, assignees, lookupUser: getUser }) : []),
    [task, assignees, getUser],
  );
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  // Who the picker offers: task members except you.
  const mentionable = useMemo(
    () => members.filter((m) => m.id !== currentUserId),
    [members, currentUserId],
  );
  const suggestions = useMemo(
    () => (mention ? filterMentionCandidates(mentionable, mention.query) : []),
    [mention, mentionable],
  );
  // Stay open while a name is being typed; a space with no match means the "@"
  // was just text, so get out of the way.
  const mentionOpen = !!mention && (suggestions.length > 0 || !/\s/.test(mention.query));

  const ordered = useMemo(
    () => [...comments].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [comments],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [mention?.start, mention?.query]);

  const syncMention = (el: HTMLTextAreaElement) => {
    const caret = el.selectionStart ?? el.value.length;
    const next = el.selectionEnd === caret ? findMentionQuery(el.value, caret) : null;
    setMention(next);
    if (!next) return;
    const coords = getCaretCoordinates(el, next.start);
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 20;
    setMentionPos({
      top: coords.top + lineHeight + 4,
      left: Math.max(0, Math.min(coords.left, el.clientWidth - MENTION_MENU_WIDTH - 8)),
    });
  };

  // Grow with the text (up to a cap), then restore any pending caret.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > COMPOSER_MAX_HEIGHT ? 'auto' : 'hidden';
    const pending = pendingCaret.current;
    if (pending) {
      pendingCaret.current = null;
      el.focus();
      el.setSelectionRange(pending.pos, pending.pos);
      if (pending.openMention) syncMention(el);
    }
  }, [draft]);

  useEffect(() => {
    if (!showEmoji) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!emojiRef.current?.contains(e.target as Node)) setShowEmoji(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEmoji(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showEmoji]);

  const insertText = (text: string, openMention = false) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    pendingCaret.current = { pos: start + text.length, openMention };
    setDraft(draft.slice(0, start) + text + draft.slice(end));
  };

  const startMention = () => {
    const before = draft.slice(0, textareaRef.current?.selectionStart ?? draft.length);
    insertText(before && !/\s$/.test(before) ? ' @' : '@', true);
  };

  const pickMention = (person: MentionCandidate) => {
    const el = textareaRef.current;
    if (!el || !mention) return;
    const next = insertMention(draft, mention.start, el.selectionStart ?? draft.length, person.name);
    pendingCaret.current = { pos: next.caret, openMention: false };
    setMention(null);
    setDraft(next.text);
  };

  // Reply = mention the author at the front, keeping whatever is already typed.
  const replyTo = (person: MentionCandidate) => {
    const tag = `@${person.name}`;
    composerRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    if (draft.includes(tag)) {
      textareaRef.current?.focus();
      return;
    }
    const rest = draft.replace(/^\s+/, '');
    const next = rest ? `${tag} ${rest}` : `${tag} `;
    pendingCaret.current = { pos: next.length, openMention: false };
    setDraft(next);
  };

  const submit = async () => {
    const text = draft.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      await tasksApi.addComment(taskId, {
        comment: text,
        mentioned_user_ids: extractMentionIds(text, mentionable),
      });
      setDraft('');
      setMention(null);
      setShowEmoji(false);
      onRefresh();
    } catch (err) {
      toast.error(formatTaskApiError(err, 'Could not post your comment'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (mentionOpen) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setMention(null);
        return;
      }
      if (suggestions.length > 0) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const step = e.key === 'ArrowDown' ? 1 : -1;
          setActiveIndex((i) => (i + step + suggestions.length) % suggestions.length);
          return;
        }
        if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey)) {
          e.preventDefault();
          pickMention(suggestions[Math.min(activeIndex, suggestions.length - 1)]);
          return;
        }
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit();
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) syncMention(e.currentTarget);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = async (commentId: string) => {
    const text = editText.trim();
    if (!text || savingEdit) return;
    setSavingEdit(true);
    try {
      await tasksApi.updateComment(commentId, { comment: text });
      cancelEdit();
      onRefresh();
    } catch (err) {
      toast.error(formatTaskApiError(err, 'Could not update the comment'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = (commentId: string) => {
    setConfirm({
      title: 'Delete this comment?',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await tasksApi.deleteComment(commentId);
          onRefresh();
        } catch (err) {
          toast.error(formatTaskApiError(err, 'Could not delete the comment'));
        }
      },
    });
  };

  const renderComment = (comment: TaskComment) => {
    const mine = comment.user_id === currentUserId;
    const member = memberById.get(comment.user_id);
    const name = comment.commenter_name || member?.name || getUserName(comment.user_id);
    const email = member?.email || getUserEmail(comment.user_id);
    const editing = editingId === comment.id;
    // Highlight full names: task members plus anyone the server recorded as mentioned.
    const mentionNames = [
      ...members.map((m) => m.name),
      ...(comment.mentioned_user_ids || []).map((id) => getUser(id)?.name || ''),
    ];

    return (
      <li
        key={comment.id}
        className="flex gap-3"
        style={{
          padding: '12px 14px',
          borderRadius: 14,
          background: 'var(--gt-surface-1)',
          border: '1px solid var(--gt-divider)',
        }}
      >
        <PersonAvatar userId={comment.user_id} name={name} size={34} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0" style={{ paddingTop: 2 }}>
              <span title={email || undefined} style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--gt-text-primary)' }}>
                {name}
              </span>
              {mine && <span className="gt-pill is-personal" style={PILL_STYLE}>You</span>}
              {member?.roles.map((role) => (
                <span key={role} className="gt-pill is-ghost" style={PILL_STYLE}>{role}</span>
              ))}
              <span
                title={new Date(comment.created_at).toLocaleString()}
                style={{ fontSize: 11.5, color: 'var(--gt-text-tertiary)' }}
              >
                {formatRelative(comment.created_at)}
                {comment.edited_at ? ' · edited' : ''}
              </span>
            </div>

            {mine && !editing && (
              <div className="flex items-center gap-0.5 shrink-0" style={{ margin: '-4px -6px 0 0' }}>
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(comment.id);
                    setEditText(comment.comment);
                  }}
                  className="gt-icon-btn is-sm"
                  title="Edit"
                  aria-label="Edit comment"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(comment.id)}
                  className="gt-icon-btn is-sm"
                  style={{ color: 'var(--gt-dnd)' }}
                  title="Delete"
                  aria-label="Delete comment"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {editing ? (
            <div className="mt-2">
              <AutoGrowTextarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    cancelEdit();
                  } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    void saveEdit(comment.id);
                  }
                }}
                autoFocus
                aria-label="Edit comment"
                className="gt-input"
                style={{ fontSize: 13.5, lineHeight: 1.55 }}
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => void saveEdit(comment.id)}
                  disabled={savingEdit || !editText.trim()}
                  className="gt-btn is-primary is-sm"
                >
                  {savingEdit ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={cancelEdit} className="gt-btn is-ghost is-sm">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p
                style={{
                  marginTop: 4,
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  color: 'var(--gt-text-primary)',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
              >
                {splitMentions(comment.comment, mentionNames).map((part, idx) =>
                  part.mention ? (
                    <span
                      key={idx}
                      style={{
                        color: 'var(--gt-text-link)',
                        background: 'rgba(16, 185, 129, 0.14)',
                        borderRadius: 6,
                        padding: '1px 5px',
                        fontWeight: 600,
                      }}
                    >
                      {part.text}
                    </span>
                  ) : (
                    <React.Fragment key={idx}>{part.text}</React.Fragment>
                  ),
                )}
              </p>

              {/* Only task members can be mentioned, so only they get a Reply. */}
              {!mine && member && (
                <button
                  type="button"
                  onClick={() => replyTo(member)}
                  className="mt-1.5 inline-flex items-center gap-1 transition-colors text-[color:var(--gt-text-tertiary)] hover:text-[color:var(--gt-primary)]"
                  style={{ fontSize: 12, fontWeight: 600 }}
                >
                  <Reply className="w-3.5 h-3.5" />
                  Reply
                </button>
              )}
            </>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="gt-card p-5 mb-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 11,
            background: 'var(--gt-grad-aurora)',
            color: '#ffffff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--gt-glow-soft), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}
        >
          <MessageSquare className="w-4 h-4" />
        </div>
        <div>
          <h3 className="gt-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--gt-text-primary)', lineHeight: 1.2 }}>
            Comments
          </h3>
          <span className="gt-page-sub gt-numerals" style={{ fontSize: 11.5, marginTop: 2, display: 'block' }}>
            {comments.length === 0
              ? 'No comments yet'
              : `${comments.length} comment${comments.length === 1 ? '' : 's'} · last activity ${formatRelative(ordered[0]?.created_at).toLowerCase()}`}
          </span>
        </div>
      </div>

      {/* Composer */}
      <div
        ref={composerRef}
        className="mb-5"
        style={{
          border: `1px solid ${focused ? 'var(--gt-primary)' : 'var(--gt-border)'}`,
          borderRadius: 'var(--gt-radius-input)',
          background: 'var(--gt-surface-0)',
          boxShadow: focused ? '0 0 0 3px rgba(16, 185, 129, 0.14)' : 'none',
          transition: 'border-color 150ms, box-shadow 150ms',
        }}
      >
        <div style={{ position: 'relative' }}>
          <textarea
            ref={textareaRef}
            value={draft}
            rows={3}
            onChange={(e) => {
              setDraft(e.target.value);
              syncMention(e.target);
            }}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onClick={(e) => syncMention(e.currentTarget)}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              setMention(null);
            }}
            placeholder="Write a comment… Type @ to mention someone on this task"
            aria-label="Write a comment"
            aria-haspopup="listbox"
            aria-expanded={mentionOpen}
            className="block w-full focus:outline-none"
            style={{
              resize: 'none',
              border: 0,
              borderRadius: 'var(--gt-radius-input) var(--gt-radius-input) 0 0',
              background: 'transparent',
              boxShadow: 'none',
              padding: '12px 14px 8px',
              minHeight: 84,
              fontFamily: 'var(--gt-font)',
              fontSize: 13.5,
              lineHeight: 1.55,
              color: 'var(--gt-text-primary)',
            }}
          />

          {mentionOpen && mention && (
            <div
              role="listbox"
              aria-label="People on this task"
              onMouseDown={(e) => e.preventDefault()}
              style={{
                position: 'absolute',
                zIndex: 40,
                top: mentionPos.top,
                left: mentionPos.left,
                width: MENTION_MENU_WIDTH,
                maxWidth: '100%',
                padding: 6,
                background: 'var(--gt-glass-mid)',
                border: '1px solid var(--gt-border)',
                borderRadius: 12,
                boxShadow: 'var(--gt-elev-3)',
              }}
            >
              <div
                style={{
                  padding: '4px 8px 6px',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--gt-text-tertiary)',
                }}
              >
                People on this task
              </div>
              {suggestions.length === 0 ? (
                <div style={{ padding: '6px 8px 8px', fontSize: 12.5, color: 'var(--gt-text-tertiary)' }}>
                  {mentionable.length === 0
                    ? 'No one else is on this task yet.'
                    : `No one on this task matches “${mention.query}”.`}
                </div>
              ) : (
                suggestions.map((person, idx) => (
                  <button
                    key={person.id}
                    type="button"
                    role="option"
                    aria-selected={idx === activeIndex}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => pickMention(person)}
                    className="w-full flex items-center gap-2.5 text-left"
                    style={{
                      padding: '7px 8px',
                      borderRadius: 9,
                      background: idx === activeIndex ? 'var(--gt-primary-container)' : 'transparent',
                      transition: 'background 120ms',
                    }}
                  >
                    <PersonAvatar userId={person.id} name={person.name} size={28} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate" style={{ fontSize: 13, fontWeight: 600, color: 'var(--gt-text-primary)' }}>
                        {person.name}
                      </span>
                      <span className="block truncate" style={{ fontSize: 11, color: 'var(--gt-text-tertiary)' }}>
                        {person.roles.join(' · ')}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div
          className="flex items-center justify-between gap-2"
          style={{ padding: '6px 8px 8px', borderTop: '1px solid var(--gt-divider)' }}
        >
          <div className="flex items-center gap-0.5 min-w-0">
            <div ref={emojiRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShowEmoji((open) => !open)}
                className="gt-icon-btn is-sm"
                title="Add emoji"
                aria-label="Add emoji"
                aria-expanded={showEmoji}
              >
                <Smile className="w-4 h-4" />
              </button>
              {showEmoji && (
                <div
                  role="dialog"
                  aria-label="Emoji"
                  onMouseDown={(e) => e.preventDefault()}
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    left: 0,
                    zIndex: 40,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(8, 32px)',
                    gap: 2,
                    padding: 6,
                    background: 'var(--gt-glass-mid)',
                    border: '1px solid var(--gt-border)',
                    borderRadius: 12,
                    boxShadow: 'var(--gt-elev-3)',
                  }}
                >
                  {QUICK_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        insertText(emoji);
                        setShowEmoji(false);
                      }}
                      className="gt-icon-btn is-sm"
                      style={{ width: 32, height: 32, fontSize: 18 }}
                      aria-label={`Insert ${emoji}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={startMention}
              className="gt-icon-btn is-sm"
              title="Mention someone on this task"
              aria-label="Mention someone on this task"
            >
              <AtSign className="w-4 h-4" />
            </button>
            <span
              className="hidden sm:inline-flex items-center gap-1 truncate"
              style={{ marginLeft: 6, fontSize: 11.5, color: 'var(--gt-text-tertiary)' }}
            >
              <kbd className="gt-kbd">{IS_MAC ? '⌘' : 'Ctrl'}</kbd>
              <kbd className="gt-kbd">Enter</kbd>
              to post
            </span>
          </div>

          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting || !draft.trim()}
            className="gt-btn is-primary is-sm shrink-0"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SendHorizontal className="w-3.5 h-3.5" />}
            {submitting ? 'Posting…' : 'Post comment'}
          </button>
        </div>
      </div>

      {/* Comments — newest first, like the Activity and Checklist tabs */}
      {ordered.length === 0 ? (
        <div className="gt-empty" style={{ padding: '36px 16px' }}>
          <div className="gt-empty-leaf"><MessageSquare className="w-6 h-6" /></div>
          <div className="gt-empty-title">No comments yet</div>
          <div className="gt-page-sub">Start the conversation — type @ to loop in someone on this task.</div>
        </div>
      ) : (
        <ul className="space-y-2.5 overflow-y-auto gt-scroll" style={{ maxHeight: 560, paddingRight: 4 }}>
          {ordered.map(renderComment)}
        </ul>
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

export default CommentSection;
